/**
 * ============================================================
 * RESPONSE INTERCEPTOR - 401 AUTO-LOGOUT
 * ============================================================
 * Forces immediate logout when server returns 401 (Unauthorized)
 *
 * ✅ STOPS: Infinite 401 retry loops
 * ✅ PREVENTS: Broken auth state
 * ✅ SECURITY: Industry-standard auth lifecycle
 *
 * Used by: Banks, healthcare apps, enterprise SaaS
 * ============================================================
 */

import api from "./client";
import axios from "axios";
import { API_URL } from "../apiConfig";

let isRefreshing = false;
let pendingRequests = [];

const resolvePending = (token) => {
  pendingRequests.forEach((cb) => cb(token));
  pendingRequests = [];
};

const rejectPending = (error) => {
  pendingRequests.forEach((cb) => cb(null, error));
  pendingRequests = [];
};

api.interceptors.response.use(
  // Success: pass through
  (response) => response,

  // Error: handle 401 specially
  (error) => {
    // ⚠️ CRITICAL: Do NOT logout on login/auth endpoints!
    const isAuthEndpoint =
      error.config?.url?.includes("/auth/login") ||
      error.config?.url?.includes("/auth/signup") ||
      error.config?.url?.includes("/auth/change-password") ||
      error.config?.url?.includes("/auth/refresh");

    const isTokenExpired =
      error.response?.status === 401 &&
      error.response?.data?.code === "TOKEN_EXPIRED";

    if (isTokenExpired && !isAuthEndpoint) {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("refreshTokenExpiresAt");
        localStorage.removeItem("user");
        localStorage.removeItem("activeLocation");
        sessionStorage.clear();
        window.location.href = "/login";
        return Promise.reject(new Error("Unauthorized - Session expired"));
      }

      if (!isRefreshing) {
        isRefreshing = true;
        axios
          .post(`${API_URL}/api/auth/refresh`, { refreshToken })
          .then((res) => {
            const newToken = res.data?.token;
            if (newToken) {
              localStorage.setItem("token", newToken);
              resolvePending(newToken);
            } else {
              rejectPending(new Error("Failed to refresh token"));
            }
          })
          .catch((refreshErr) => {
            rejectPending(refreshErr);
          })
          .finally(() => {
            isRefreshing = false;
          });
      }

      return new Promise((resolve, reject) => {
        pendingRequests.push((token, err) => {
          if (!token) {
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("refreshTokenExpiresAt");
            localStorage.removeItem("user");
            localStorage.removeItem("activeLocation");
            sessionStorage.clear();
            window.location.href = "/login";
            reject(err || new Error("Unauthorized - Session expired"));
            return;
          }
          const retryConfig = {
            ...error.config,
            headers: {
              ...(error.config?.headers || {}),
              Authorization: `Bearer ${token}`,
            },
          };
          resolve(api.request(retryConfig));
        });
      });
    }

    if (error.response?.status === 401 && !isAuthEndpoint) {
      console.error("❌ [Auth] 401 Unauthorized - Forcing logout");
      console.log("   Endpoint:", error.config?.url);

      // Clear ALL auth data
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("refreshTokenExpiresAt");
      localStorage.removeItem("user");
      localStorage.removeItem("activeLocation");
      sessionStorage.clear();

      // Force redirect to login
      // Using window.location to ensure clean state
      window.location.href = "/login";

      // Return rejected promise to prevent further processing
      return Promise.reject(new Error("Unauthorized - Session expired"));
    }

    // For other errors (including 401 on auth endpoints), pass through
    return Promise.reject(error);
  },
);

console.log("✅ [Auth] Response interceptor initialized (401 auto-logout)");

export default api;
